// Cambio di tema con un'onda di colore che parte dal pulsante e si allarga fino a coprire tutta la pagina (View Transitions API): il vecchio tema resta fermo come
// un'immagine e il nuovo si scopre dentro un cerchio che cresce dal controllo, a velocità costante e con il bordo sfumato (maschera, non ritaglio netto). Dove
// l'API manca (Firefox prima della 144, browser vecchi) o l'utente chiede meno movimento, il tema cambia di colpo come prima. Le regole CSS dei pseudo-elementi
// stanno in `base/base.css`. Niente variabili CSS sulla radice e niente stili scritti su `<html>` durante l'onda: sarebbero ereditati e farebbero ricalcolare
// gli stili di tutta la pagina, con uno scatto a fine onda.

/** Durata dell'onda: dal pulsante all'angolo più lontano. */
export const DURATA_ONDA_MS = 1000;
/** Larghezza del bordo sfumato dell'onda. Deve coincidere con i 140px della maschera in `base/base.css`. */
export const SFUMATURA_PX = 140;
// Velocità costante: il fronte avanza in modo uniforme
const EASING_ONDA = "linear";
// L'animazione deve TENERE lo stato finale (`fill: "forwards"`): il nuovo tema parte nascosto per CSS (maschera di dimensione 0), quindi appena l'animazione finisce, e
// prima che il browser smonti la transizione, tornerebbe lo stile di base e per un frame si rivedrebbe il vecchio tema (il lampo di fine onda).

interface TransizioneVista {
  ready: Promise<void>;
  finished: Promise<void>;
  skipTransition?: () => void;
}
type DocumentoConTransizioni = Document & { startViewTransition?: (aggiorna: () => void | Promise<void>) => TransizioneVista };

const movimentoRidotto = (): boolean => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const conMaschere = (): boolean => typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("mask-image", "linear-gradient(#000, #000)");

/** Raggio del cerchio che, partendo da (x, y), copre tutta la finestra: la distanza dall'angolo più lontano. */
export function raggioCopertura(x: number, y: number, larghezza: number, altezza: number): number {
  return Math.hypot(Math.max(x, larghezza - x), Math.max(y, altezza - y));
}

/** Lato del quadrato della maschera a fine onda: il cerchio coperto da `raggio` più il bordo sfumato, così l'angolo più lontano è già del tutto scoperto. */
export function latoMaschera(raggio: number): number {
  return 2 * (raggio + SFUMATURA_PX);
}

export function useCambioTema() {
  const ui = useUiStore();
  let inCorso = false;

  /** Porta il tema a `scuro`; con `origine` (di solito il controllo premuto) l'onda parte dal suo centro. */
  async function cambiaTema(scuro: boolean, origine?: Element | null): Promise<void> {
    if (scuro === ui.isDarkTheme) return;
    const documento = document as DocumentoConTransizioni;
    // click ripetuti mentre l'onda è in corso: il tema cambia subito, senza sovrapporre due transizioni
    if (!import.meta.client || typeof documento.startViewTransition !== "function" || movimentoRidotto() || inCorso) {
      ui.impostaTema(scuro);
      return;
    }
    const riquadro = origine?.getBoundingClientRect();
    const x = riquadro ? riquadro.left + riquadro.width / 2 : window.innerWidth - 40;
    const y = riquadro ? riquadro.top + riquadro.height / 2 : 24;
    const raggio = raggioCopertura(x, y, window.innerWidth, window.innerHeight);

    inCorso = true;
    // il DOM deve avere il tema nuovo quando il callback finisce: l'immagine «nuova» si scatta allora
    const transizione = documento.startViewTransition(async () => {
      ui.impostaTema(scuro);
      await nextTick();
    });
    try {
      await transizione.ready;
      if (conMaschere()) {
        // la maschera (dimensione 0 per CSS) cresce restando centrata sull'origine: dimensione e posizione si animano insieme, con la stessa curva
        const lato = latoMaschera(raggio);
        document.documentElement.animate(
          { maskSize: ["0px 0px", `${lato}px ${lato}px`], maskPosition: [`${x}px ${y}px`, `${x - lato / 2}px ${y - lato / 2}px`] },
          { duration: DURATA_ONDA_MS, easing: EASING_ONDA, fill: "forwards", pseudoElement: "::view-transition-new(root)" },
        );
      } else {
        // browser senza maschere: ritaglio circolare, con il bordo netto
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${raggio}px at ${x}px ${y}px)`] },
          { duration: DURATA_ONDA_MS, easing: EASING_ONDA, fill: "forwards", pseudoElement: "::view-transition-new(root)" },
        );
      }
      await transizione.finished;
    } catch {
      // transizione saltata (scheda nascosta, nuova transizione) o animazione non avviabile: il tema è già cambiato nel callback; si chiude la transizione per non
      // lasciare il nuovo tema nascosto
      transizione.skipTransition?.();
    } finally {
      inCorso = false;
    }
  }

  return { cambiaTema };
}
